import React from 'react';

interface Column<T> {
  key: string;
  label: string;
  render?: (row: T, index: number) => React.ReactNode;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  emptyMessage?: string;
  rowKey: (row: T, index: number) => string;
}

function DataTable<T>({ columns, rows, loading, emptyMessage = 'No data available', rowKey }: DataTableProps<T>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-10 h-10 border-4 border-trident-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="text-center py-16 text-trident-gray-500">
        <div className="w-14 h-14 rounded-full bg-trident-gray-100 flex items-center justify-center mx-auto mb-3">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        </div>
        <p className="font-display font-semibold text-trident-gray-700">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-trident-gray-100 bg-white">
      <table className="w-full">
        <thead>
          <tr className="bg-trident-gray-50 border-b border-trident-gray-100">
            {columns.map(col => (
              <th
                key={col.key}
                style={col.width ? { width: col.width } : undefined}
                className={`py-3 px-4 font-display font-bold text-xs uppercase tracking-wider text-trident-gray-500 ${
                  col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'
                }`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={rowKey(row, i)}
              className="border-b border-trident-gray-50 last:border-b-0 hover:bg-trident-orange-light/30 transition-colors"
            >
              {columns.map(col => (
                <td
                  key={col.key}
                  className={`py-3 px-4 text-sm text-trident-gray-700 ${
                    col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'
                  }`}
                >
                  {col.render ? col.render(row, i) : (row as any)[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default DataTable;
